
import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useContractTypes } from "@/hooks/useContractTypes";
import { toast } from "sonner";
import { ContractType } from "@/lib/types";

interface ContractTypeDeleteDialogProps {
  contractType: ContractType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContractTypeDeleteDialog({
  contractType,
  open,
  onOpenChange,
}: ContractTypeDeleteDialogProps) {
  const [showReassignOption, setShowReassignOption] = useState(false);
  const [targetContractTypeId, setTargetContractTypeId] = useState<string>("");
  const [isReassigning, setIsReassigning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { 
    useContractTypesQuery, 
    useContractTypeUsageQuery, 
    useDeleteContractTypeMutation,
    useReassignContractTypeMutation
  } = useContractTypes();
  
  const { data: contractTypes = [] } = useContractTypesQuery();
  const { 
    data: usageInfo, 
    isLoading: isLoadingUsage, 
    refetch: refetchUsage 
  } = useContractTypeUsageQuery(contractType.id);
  
  const deleteContractType = useDeleteContractTypeMutation();
  const reassignContractType = useReassignContractTypeMutation();

  // Force refetch usage data when dialog opens
  useEffect(() => {
    if (open) {
      refetchUsage();
    }
  }, [open, refetchUsage]);

  // Filter out the current contract type from the options
  const availableContractTypes = contractTypes.filter(type => type.id !== contractType.id);

  // Set the first available contract type as the default target
  useEffect(() => {
    if (availableContractTypes.length > 0 && !targetContractTypeId) {
      setTargetContractTypeId(availableContractTypes[0].id);
    }
  }, [availableContractTypes, targetContractTypeId]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteContractType.mutateAsync(contractType.id);
      toast.success(`Contract type "${contractType.name}" deleted successfully`);
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to delete contract type: ${(error as Error).message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReassign = async () => {
    if (!targetContractTypeId) {
      toast.error("Please select a target contract type");
      return;
    }

    try {
      setIsReassigning(true);
      await reassignContractType.mutateAsync({
        fromId: contractType.id,
        toId: targetContractTypeId
      });
      
      toast.success(`Contracts reassigned successfully`);
      handleDelete();
    } catch (error) {
      toast.error(`Failed to reassign contracts: ${(error as Error).message}`);
      setIsReassigning(false);
    }
  };

  const contractCount = usageInfo?.count || 0;
  console.log("Contract usage info:", usageInfo, "Count:", contractCount);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Contract Type</AlertDialogTitle>
          <AlertDialogDescription>
            {isLoadingUsage ? (
              <div className="flex items-center space-x-2 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Checking usage...</span>
              </div>
            ) : contractCount > 0 ? (
              <>
                <p className="mb-2 font-medium text-destructive">
                  Warning: This contract type is currently used by <strong>{contractCount}</strong> contract{contractCount !== 1 ? 's' : ''}.
                </p>
                <p>
                  Deleting this contract type will affect all associated contracts. 
                  You can either reassign these contracts to another type before deleting, 
                  or delete without reassigning.
                </p>
              </>
            ) : (
              <p>
                Are you sure you want to delete the contract type "{contractType.name}"?
                This action cannot be undone.
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {contractCount > 0 && !showReassignOption && (
          <div className="flex flex-col space-y-2 py-2">
            <Button 
              variant="outline" 
              onClick={() => setShowReassignOption(true)}
              className="w-full"
            >
              Reassign contracts before deleting
            </Button>
          </div>
        )}

        {showReassignOption && contractCount > 0 && (
          <div className="flex flex-col space-y-3 py-2">
            <p className="text-sm font-medium">
              Select a contract type to reassign the {contractCount} contract{contractCount !== 1 ? 's' : ''}:
            </p>
            
            {availableContractTypes.length > 0 ? (
              <Select
                value={targetContractTypeId}
                onValueChange={setTargetContractTypeId}
                disabled={isReassigning}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a contract type" />
                </SelectTrigger>
                <SelectContent>
                  {availableContractTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">
                No other contract types available. Please create another contract type first.
              </p>
            )}

            <Button
              variant="default"
              disabled={!targetContractTypeId || isReassigning || availableContractTypes.length === 0}
              onClick={handleReassign}
              className="w-full"
            >
              {isReassigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reassigning...
                </>
              ) : (
                "Reassign and Delete"
              )}
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowReassignOption(false)}
              disabled={isReassigning}
              className="w-full"
            >
              Cancel Reassignment
            </Button>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting || isReassigning}>Cancel</AlertDialogCancel>
          {!showReassignOption && (
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={isDeleting}
              className={contractCount > 0 ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                contractCount > 0 ? "Delete Anyway" : "Delete"
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
